import React, { useState, useMemo, useEffect } from "react";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import SearchBar from "../../../../Components/Utils/SearchBar";
import {
  FilterButton,
  ImportButton,
} from "../../../../Components/Utils/Buttons";
import UseGetDataSearch from "../../../../Components/Hooks/useFetchDataBySearch";
import PremiumTable, { CopyButton } from "../../../../Components/Others/PremiumTable";
import NuAmount from "../../../../Components/Utils/NuAmount";
import ViewToggle from "../../../../Components/Utils/ViewToggle";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";
import ImportModal from "./ImportModal";
import FilterModal from "./FilterModal";

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  if (typeof val === "number") return isNaN(val) ? 0 : val;
  const cleaned = String(val).replace(/,/g, "").trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// Safe extractors for the ageing buckets
const getAccountName = (row) =>
  row.account ||
  row.account_name ||
  row.accountName ||
  row.company ||
  row.company_name ||
  row.name ||
  row["Account"] ||
  "-";

const get0_30 = (row) =>
  parseNum(
    row.days_0_30 ??
      row["( 0 - 30 ) Days"] ??
      row["( 0 - 30 )"] ??
      row["0 - 30"] ??
      row["0-30"] ??
      row.days0_30 ??
      row.range_0_30
  );

const get31_60 = (row) =>
  parseNum(
    row.days_31_60 ??
      row["( 31 - 60 ) Days"] ??
      row["( 31 - 60 )"] ??
      row["31 - 60"] ??
      row["31-60"] ??
      row.days31_60 ??
      row.range_31_60
  );

const get61_90 = (row) =>
  parseNum(
    row.days_61_90 ??
      row["( 61 - 90 ) Days"] ??
      row["( 61 - 90 )"] ??
      row["61 - 90"] ??
      row["61-90"] ??
      row.days61_90 ??
      row.range_61_90
  );

const get91_120 = (row) =>
  parseNum(
    row.days_91_120 ??
      row["( 91 - 120 ) Days"] ??
      row["( 91 - 120 )"] ??
      row["91 - 120"] ??
      row["91-120"] ??
      row.days91_120 ??
      row.range_91_120
  );

const get121_150 = (row) =>
  parseNum(
    row.days_121_150 ??
      row["( 121 - 150 ) Days"] ??
      row["( 121 - 150 )"] ??
      row["121 - 150"] ??
      row["121-150"] ??
      row["121 - 150 ) Days"] ??
      row.days121_150 ??
      row.range_121_150
  );

const get151Plus = (row) =>
  parseNum(
    row.days_151_plus ??
      row.days_gte_151 ??
      row["(>=151) Days"] ??
      row["(>=151)"] ??
      row[">=151"] ??
      row.days151_plus ??
      row.range_151_plus
  );

const getTotalAmt = (row) => {
  const calculated =
    get0_30(row) +
    get31_60(row) +
    get61_90(row) +
    get91_120(row) +
    get121_150(row) +
    get151Plus(row);
  const explicit = parseNum(
    row.total_amt ??
      row.total_amount ??
      row.totalAmt ??
      row["Total Amt."] ??
      row["Total Amt"] ??
      row.total
  );
  return explicit > 0 ? explicit : calculated;
};

export default function AgeingAnalysisIndex() {
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    Number(localStorage.getItem("perPageView")) || 20
  );
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("ageingViewMode") || "table"
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const [showImportModal, setShowImportModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [filterValues, setFilterValues] = useState({
    min_total: "",
    max_total: "",
    from_date: "",
    to_date: "",
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const effectiveViewMode = isMobile
    ? "card"
    : viewMode === "list"
    ? "table"
    : viewMode;

  const isFiltered = Boolean(
    filterValues.min_total ||
      filterValues.max_total ||
      filterValues.from_date ||
      filterValues.to_date
  );

  const queryParams = useMemo(() => {
    const params = {
      page: pageIndex,
      per_page: itemsPerPage,
      limit: itemsPerPage,
    };
    if (searchQuery) params.search = searchQuery;
    if (filterValues.min_total) params.min_total = filterValues.min_total;
    if (filterValues.max_total) params.max_total = filterValues.max_total;
    if (filterValues.from_date) params.from_date = filterValues.from_date;
    if (filterValues.to_date) params.to_date = filterValues.to_date;

    return params;
  }, [searchQuery, pageIndex, itemsPerPage, filterValues]);

  const { data, totalCount, getData, dataLoading } = UseGetDataSearch(
    "/ageing-analysis/",
    queryParams
  );

  const records = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.items)) return data.items;
    return [];
  }, [data]);

  const handleApplyFilter = (newValues) => {
    setFilterValues(newValues);
    setPageIndex(1);
  };

  const handleClearFilter = () => {
    setFilterValues({
      min_total: "",
      max_total: "",
      from_date: "",
      to_date: "",
    });
    setPageIndex(1);
  };

  const headers = [
    { title: "S.No", width: "5%", textAlign: "center", rowAlign: "center", align: "center" },
    { title: "Account", width: "20%", textAlign: "left", rowAlign: "left", align: "left" },
    { title: "( 0 - 30 ) Days", width: "11%", textAlign: "center", rowAlign: "center", align: "center" },
    { title: "( 31 - 60 ) Days", width: "11%", textAlign: "center", rowAlign: "center", align: "center" },
    { title: "( 61 - 90 ) Days", width: "11%", textAlign: "center", rowAlign: "center", align: "center" },
    { title: "( 91 - 120 ) Days", width: "11%", textAlign: "center", rowAlign: "center", align: "center" },
    { title: "( 121 - 150 ) Days", width: "11%", textAlign: "center", rowAlign: "center", align: "center" },
    { title: "(>=151) Days", width: "11%", textAlign: "center", rowAlign: "center", align: "center" },
    { title: "Total Amt.", width: "12%", textAlign: "center", rowAlign: "center", align: "center" },
  ];

  return (
    <div className="w-full h-full p-2 sm:px-4">
      {/* Top Header Bar */}
      <div className="w-full flex flex-col md:flex-row justify-between items-stretch md:items-center gap-2 md:gap-3 mb-3">
        <div className="w-full md:w-auto flex justify-between items-center gap-2">
          <Breadcrumb
            items={[
              { name: "Payment" },
              { name: "Ageing Analysis" },
            ]}
          />
          <div className="flex md:hidden items-center gap-2">
            <FilterButton handleClick={() => setShowFilterModal(true)} />
            {isFiltered && <FilterButton handleClick={handleClearFilter} label="Clear" />}
            {(GetAccess("ageing_analysis_create") || GetRole()) && (
              <ImportButton handleClick={() => setShowImportModal(true)} />
            )}
          </div>
        </div>

        <div className="w-full md:w-auto flex flex-wrap md:flex-nowrap items-center gap-2">
          <div className="w-full md:w-fit">
            <SearchBar
              handleChange={(e) => {
                setSearchQuery(e.target.value);
                setPageIndex(1);
              }}
              value={searchQuery}
              placeholder="Search..."
            />
          </div>
          <div className="hidden md:flex items-center gap-2">
            {/* <FilterButton handleClick={() => setShowFilterModal(true)} />
            {isFiltered && <FilterButton handleClick={handleClearFilter} label="Clear" />} */}
            {(GetAccess("ageing_analysis_create") || GetRole()) && (
              <ImportButton handleClick={() => setShowImportModal(true)} />
            )}
          </div>
          <ViewToggle
            viewMode={effectiveViewMode}
            onViewChange={(mode) => {
              setViewMode(mode);
              localStorage.setItem("ageingViewMode", mode);
            }}
            storageKey="ageingViewMode"
          />
        </div>
      </div>

      {/* Main Table / Card View */}
      <PremiumTable
        data={records}
        total={totalCount || records.length}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        dataCount={itemsPerPage}
        setDataCount={setItemsPerPage}
        dataLoading={dataLoading}
        viewMode={effectiveViewMode}
        headers={headers}
        renderCard={(row, index) => {
          const accName = getAccountName(row);
          const total = getTotalAmt(row);
          return (
            <div
              key={row.id || index}
              className="bg-white rounded-xl border border-gray-200/80 p-4 shadow-xs flex flex-col justify-between"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="text-xs font-semibold text-gray-500 mr-2">
                    #{(pageIndex - 1) * itemsPerPage + index + 1}
                  </span>
                  <span className="text-sm font-bold text-gray-800">
                    {accName}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-bold text-[#048178]">
                    <NuAmount value={total} />
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100 text-xs">
                <div>
                  <span className="text-[10px] text-gray-500 block">0-30 Days</span>
                  <span className="font-semibold text-gray-700">
                    <NuAmount value={get0_30(row)} />
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-500 block">31-60 Days</span>
                  <span className="font-semibold text-gray-700">
                    <NuAmount value={get31_60(row)} />
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-500 block">61-90 Days</span>
                  <span className="font-semibold text-gray-700">
                    <NuAmount value={get61_90(row)} />
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-500 block">91-120 Days</span>
                  <span className="font-semibold text-gray-700">
                    <NuAmount value={get91_120(row)} />
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-500 block">121-150 Days</span>
                  <span className="font-semibold text-gray-700">
                    <NuAmount value={get121_150(row)} />
                  </span>
                </div>
                <div>
                  <span className="text-[10px] text-gray-500 block">&gt;=151 Days</span>
                  <span className="font-semibold text-gray-700">
                    <NuAmount value={get151Plus(row)} />
                  </span>
                </div>
              </div>
            </div>
          );
        }}
        renderRow={(row, index) => {
          const accName = getAccountName(row);
          const val0_30 = get0_30(row);
          const val31_60 = get31_60(row);
          const val61_90 = get61_90(row);
          const val91_120 = get91_120(row);
          const val121_150 = get121_150(row);
          const val151Plus = get151Plus(row);
          const total = getTotalAmt(row);

          return (
            <tr
              key={row.id || index}
              className="bg-white hover:bg-gray-50/50 transition-colors align-middle"
            >
              {/* Index */}
              <td className="p-4 text-center align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl text-xs font-semibold text-gray-500">
                {(pageIndex - 1) * itemsPerPage + index + 1}
              </td>

              {/* Account */}
              <td className="p-4 align-middle border-t border-b border-gray-200/60 font-semibold text-xs text-gray-800 max-w-xs">
                <div className="flex items-center gap-1">
                  <span className="truncate">{accName}</span>
                  <CopyButton text={accName} />
                </div>
              </td>

              {/* 0 - 30 Days */}
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">
                {val0_30 > 0 ? (
                  <NuAmount value={val0_30} />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>

              {/* 31 - 60 Days */}
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">
                {val31_60 > 0 ? (
                  <NuAmount value={val31_60} />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>

              {/* 61 - 90 Days */}
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">
                {val61_90 > 0 ? (
                  <NuAmount value={val61_90} />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>

              {/* 91 - 120 Days */}
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">
                {val91_120 > 0 ? (
                  <NuAmount value={val91_120} />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>

              {/* 121 - 150 Days */}
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">
                {val121_150 > 0 ? (
                  <NuAmount value={val121_150} />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>

              {/* >= 151 Days */}
              <td className="p-4 align-middle border-t border-b border-gray-200/60 text-center text-xs font-semibold text-gray-700 whitespace-nowrap">
                {val151Plus > 0 ? (
                  <NuAmount value={val151Plus} />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>

              {/* Total Amt. */}
              <td className="p-4 align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl text-center text-xs font-bold text-gray-900 whitespace-nowrap">
                <NuAmount value={total} />
              </td>
            </tr>
          );
        }}
      />

      {/* Modals */}
      <ImportModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={getData}
      />

      <FilterModal
        show={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filterValues={filterValues}
        onApply={handleApplyFilter}
        onClear={handleClearFilter}
      />
    </div>
  );
}
